import React, { useCallback, useEffect, useState } from "react";
import { enumLabel } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api } from "@/lib/api";
import { isAppOnlyLink, JOIN_WORKER_PATH } from "@/lib/appLinks";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function NotificationBell() {
  const { t, auth, lang } = useApp();
  // A notification may carry Hindi text beside the English (`title_hi`/`body_hi`); `""` = not
  // translated, so a Hindi screen falls back to the English rather than showing nothing.
  const pick = (en, hi) => (lang === "hi" && hi ? hi : en);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 15 } });
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) {
      /* ignore polling errors */
    }
  }, []);

  useEffect(() => {
    if (!auth) return;
    load();
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [auth, load]);

  if (!auth) return null;

  const open = async (item) => {
    await api.post(`/notifications/${item.id}/read`);
    await load();
    // Worker deep links (/headsup/…, /worker/…) belong to the Business app; the web has no such
    // route, so send the person to the explainer rather than a silent bounce to the city home.
    if (item.link) navigate(isAppOnlyLink(item.link) ? JOIN_WORKER_PATH : item.link);
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-11 min-w-11 rounded-full"
          aria-label={unread > 0 ? `${t("notifications.title")} (${unread} ${t("notifications.unread")})` : t("notifications.title")}
          data-testid="notification-bell"
        >
          <Bell size={17} />
          {unread > 0 && (
            <span
              data-testid="notification-unread-count"
              className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[hsl(var(--accent))] px-1 text-[10px] tabular-nums font-semibold text-[hsl(var(--accent-foreground))]"
            >
              {unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notification-list">
        <DropdownMenuLabel className="flex items-center justify-between">
          {t("notifications.title")}
          {unread > 0 && (
            <button
              className="text-xs font-normal text-[hsl(var(--primary))]"
              data-testid="notification-mark-all"
              onClick={async () => {
                await api.post("/notifications/read-all");
                await load();
              }}
            >
              {t("notifications.markAllRead")}
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground" data-testid="notification-empty">
            {t("notifications.empty")}
          </p>
        )}
        {items.map((item) => (
          <DropdownMenuItem
            key={item.id}
            data-testid={`notification-${item.id}`}
            className="flex-col items-start gap-0.5"
            onClick={() => open(item)}
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid={`notification-kind-${item.id}`}>
              {enumLabel(t, "notifications.kind", item.kind)}
            </span>
            <span className={`text-sm ${item.is_read ? "text-muted-foreground" : "font-medium"}`}>{pick(item.title, item.title_hi)}</span>
            <span className="text-xs text-muted-foreground">{pick(item.body, item.body_hi)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
